import configService from "@/services/config";
import { API } from "./apiEndPoints";

/**
 * Build the full POST URL for the IQ name-only (pre-create) role Context
 * suggest operation (ER-12070, `suggest_context_role_name/`).
 * @param {string} iqBaseUrl
 * @returns {string}
 */
export function buildSuggestContextRoleEndpoint(iqBaseUrl) {
  return `${String(iqBaseUrl || "")
    .trim()
    .replace(/\/$/, "")}/${API.SUGGEST_CONTEXT_ROLE_NAME}`;
}

/**
 * Build the POST body for the name-only role Context suggestion.
 * @param {Object} params
 * @param {number|string} params.accountId
 * @param {string} params.name - typed role name (3-120 chars)
 * @returns {Object}
 */
export function buildSuggestContextRolePayload({ accountId, name }) {
  return {
    account_id: Number(accountId),
    name: String(name || "").trim(),
  };
}

/**
 * Extract the final Context text from a `complete` (or terminal) event payload.
 * The IQ service may wrap the text in a few different shapes
 * (`{context}`, `{result: {context}}`, `{response}`), so we accept all of them
 * rather than assuming one envelope.
 */
function extractCompleteContext(data) {
  if (data == null) return null;

  if (typeof data === "string") {
    const value = data.trim();
    return value || null;
  }

  if (typeof data.context === "string" && data.context.trim()) {
    return data.context.trim();
  }

  const result = data.result;
  if (result != null) {
    if (typeof result === "string" && result.trim()) return result.trim();
    if (typeof result.context === "string" && result.context.trim()) {
      return result.context.trim();
    }
  }

  const response = data.response;
  if (response != null) {
    if (typeof response === "string" && response.trim()) return response.trim();
    if (typeof response.context === "string" && response.context.trim()) {
      return response.context.trim();
    }
  }

  if (data.result?.response?.context != null) {
    return String(data.result.response.context).trim() || null;
  }

  return null;
}

/**
 * Extract a streamed text chunk from a `text` event payload. The payload is
 * usually `{text: "..."}` but a bare string is also accepted.
 */
function extractTextChunk(data) {
  if (typeof data === "string") return data;
  if (data && typeof data.text === "string") return data.text;
  return "";
}

/**
 * Extract the distilled narrative from a `thinking` event. Unlike `thought`
 * (raw Gemini reasoning — never surfaced), `thinking` is the human-readable
 * summary IQ chooses to share with the user.
 */
function extractThinkingText(data) {
  if (typeof data === "string") return data;
  if (data && typeof data.text === "string") return data.text;
  if (data && typeof data.reasoning === "string") return data.reasoning;
  return "";
}

/**
 * Extract the current phase label from a `phase` event. The service may send
 * `{phase: "…"}`, `{label: "…"}`, `{name: "…"}` or a bare string.
 */
function extractPhaseLabel(data) {
  if (typeof data === "string") return data.trim();
  if (data == null) return "";
  if (typeof data.phase === "string" && data.phase.trim()) return data.phase.trim();
  if (typeof data.label === "string" && data.label.trim()) return data.label.trim();
  if (typeof data.name === "string" && data.name.trim()) return data.name.trim();
  return "";
}

/**
 * Parse one Server-Sent Event line emitted by the suggest-context stream.
 *
 * The IQ microservice emits `thought` (raw Gemini reasoning — ignored here),
 * `text` (the actual Context chunk), `complete` (`{"context": "..."}`),
 * `error`, and the richer streaming signals `thinking`, `phase` and `stalled`.
 * The returned state exposes `context` (accumulated text), `thinking`
 * (accumulated distilled narrative), `phase` (latest step label), `stalled`
 * (silence watchdog fired) and `lastEvent` (the event just processed, so the
 * caller can emit the matching callback).
 *
 * @param {string} line - one raw SSE line
 * @param {{ currentEvent: string, context: string, thinking: string, phase: string, stalled: boolean, done: boolean, error: string|null, lastEvent: string|null }} state
 */
function parseContextSSELine(line, state) {
  const trimmed = line.trim();
  // Clear the "last processed event" on every non-data line (empty lines are the
  // SSE event boundary and `event:` lines only advance the parser). This keeps
  // the callback dispatch in `handleLine` from re-firing the previous event when
  // a blank/event line passes through the buffer splitter.
  if (!trimmed) return { ...state, lastEvent: null };

  if (trimmed.startsWith("event:")) {
    return {
      ...state,
      currentEvent: trimmed.slice(6).trim().toLowerCase(),
      lastEvent: null,
    };
  }
  if (!trimmed.startsWith("data:")) return { ...state, lastEvent: null };

  const dataStr = trimmed.slice(5).trim();
  if (!dataStr) return { ...state, lastEvent: null };

  let data;
  try {
    data = JSON.parse(dataStr);
  } catch {
    // Some emitters send plain text rather than JSON.
    data = dataStr;
  }

  const eventType = state.currentEvent || "";
  const next = { ...state, currentEvent: "", lastEvent: eventType || null };

  if (eventType === "text") {
    const chunk = extractTextChunk(data);
    if (chunk) next.context = state.context + chunk;
    return next;
  }

  if (eventType === "complete") {
    const completeContext = extractCompleteContext(data);
    next.done = true;
    if (completeContext) next.context = completeContext;
    return next;
  }

  if (eventType === "error") {
    const message =
      data && typeof data.error === "string" && data.error.trim()
        ? data.error.trim()
        : "Context suggestion failed.";
    next.error = message;
    next.done = true;
    return next;
  }

  // `thinking` — distilled narrative (NOT the raw `thought` reasoning, which
  // the standard forbids showing to the user).
  if (eventType === "thinking") {
    const text = extractThinkingText(data);
    if (text) next.thinking = (state.thinking || "") + text;
    return next;
  }

  // `phase` — the distilled step name IQ is currently working on.
  if (eventType === "phase") {
    const label = extractPhaseLabel(data);
    if (label) next.phase = label;
    return next;
  }

  // `stalled` — silence watchdog: IQ has not produced a token for a while.
  if (eventType === "stalled") {
    next.stalled = true;
    return next;
  }

  // `thought` and any unknown event are ignored (reasoning is not Context).
  return next;
}

/**
 * Stream a name-only (pre-create) role Context suggestion from IQ.
 *
 * POSTs the typed role name and the account id to `/suggest_context_role_name/`
 * and accumulates the streamed `text` chunks into the returned context. The
 * caller can abort the stream (the dialog's Stop button) and keep whatever text
 * was already streamed — the partial text is surfaced through `onChunk`.
 *
 * Beyond the text, the stream also carries the UI signals used to drive the
 * IQThinkingBannerStreaming state machine: `thinking` (distilled narrative),
 * `phase` (step label) and `stalled` (silence watchdog). Each is surfaced only
 * when its callback is provided; `thought` events are never surfaced.
 *
 * @param {Object} params
 * @param {number|string} params.accountId
 * @param {string} params.name
 * @param {AbortSignal} [params.signal]
 * @param {Function} [params.onChunk] - called with the accumulated context text
 * @param {Function} [params.onComplete] - called with the final context text
 * @param {Function} [params.onError] - called with an error message
 * @param {Function} [params.onThinking] - called with the accumulated distilled narrative
 * @param {Function} [params.onPhase] - called with the latest phase label
 * @param {Function} [params.onStalled] - called when the silence watchdog fires
 * @returns {Promise<string>} the final context text
 */
export async function suggestContextRoleStream({
  accountId,
  name,
  signal,
  onChunk,
  onComplete,
  onError,
  onThinking,
  onPhase,
  onStalled,
}) {
  const iqBaseUrl = configService.get("iqBaseUrl");
  const url = buildSuggestContextRoleEndpoint(iqBaseUrl);
  const payload = buildSuggestContextRolePayload({ accountId, name });

  const token = localStorage.getItem("token");
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Token ${token}` } : {}),
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Context suggestion failed (${response.status}).`);
  }

  const contentType = String(response.headers.get("content-type") || "").toLowerCase();

  // Fallback for a plain (non-streamed) JSON response such as {"context": "…"}.
  if (!contentType.includes("text/event-stream") && response.body) {
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
    const finalContext = (extractCompleteContext(parsed) || "").trim();
    if (!finalContext && parsed && typeof parsed === "object" && parsed.error) {
      const message =
        typeof parsed.error === "string" && parsed.error.trim()
          ? parsed.error.trim()
          : "Context suggestion failed.";
      if (onError) onError(message);
      throw new Error(message);
    }
    if (onComplete) onComplete(finalContext);
    return finalContext;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let state = {
    currentEvent: "",
    context: "",
    thinking: "",
    phase: "",
    stalled: false,
    done: false,
    error: null,
    lastEvent: null,
  };
  let buffer = "";

  const handleLine = (line) => {
    state = parseContextSSELine(line, state);

    if (state.lastEvent === "text" && onChunk && state.context) {
      onChunk(state.context);
    } else if (state.lastEvent === "thinking" && onThinking) {
      onThinking(state.thinking);
    } else if (state.lastEvent === "phase" && onPhase) {
      onPhase(state.phase);
    } else if (state.lastEvent === "stalled" && onStalled) {
      onStalled();
    }

    if (state.error) {
      if (onError) onError(state.error);
      throw new Error(state.error);
    }
  };

  try {
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;
      if (done) break;

      buffer += decoder.decode(result.value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        handleLine(line);
      }
    }

    // Flush any final line that was not newline-terminated.
    if (buffer.trim()) {
      handleLine(buffer);
    }
  } finally {
    reader.releaseLock();
  }

  if (state.error) {
    throw new Error(state.error);
  }

  const finalContext = state.context.trim();
  if (onComplete) onComplete(finalContext);
  return finalContext;
}
